<template>
    <div>
        <b-row>
            <b-col class="d-flex justify-content-between align-items-center">
                <text-header id="atmo-protocol-completion" class="atmo-hash-jump-point">Подведение итогов закупочной сессии</text-header>
            </b-col>
        </b-row>
        <div v-if="!loading">
            <b-table :fields="fields" :items="proposals" class="table-atmo overflow-hidden mb-2" show-empty small responsive>
                <template #cell(index)="data">
                    {{ data.index + 1 }}
                </template>
                <template #cell(status)="data">
                    {{ data.item.status.title }} <span v-if="data.item.is_my_proposal" v-text="'(Моё)'" />
                </template>
                <template #cell(created_at)="data">
                    {{ $formatDateTime(data.item.created_at) }}
                </template>
                <template #cell(supplier)="data">
                    <b-icon-award v-if="data.item.id === selectedProposalId" style="color: var(--orange);" title="Выбран победителем" />&nbsp;
                    <router-link class="atmo-button-link" target="_blank" :to="{ name: 'OrganizationShow', params: { id: data.item.supplier.id } }">{{ data.item.supplier.name }}</router-link>
                </template>
                <template #cell(rating)="data">
                    {{ $formatPrice(data.item.supplier.rating) }}
                </template>
                <template #cell(actions)="data">
                    <action-button-small icon="clipboard-check" title="Информация о подписи" @click="showSign(false, data.item.signature)" />
                    <action-button-small v-if="data.item.signature.poa_number && data.item.signature.poa_check_link" class="ml-1" icon="clipboard-plus" title="Информация о МЧД" variant="warning" @click="showSign(false, data.item.signature)" />
                    <action-button-small icon="cloud-download" title="Скачать xml файл" variant="outline" class="ml-1" @click="downloadXml(data.item.signature.xml, false, `proposal_${data.item.id}`)" />
                    <b-button class="btn-card-action btn-card-action-green text-uppercase ml-1" style="height: 24px; padding-top: 5px !important; padding-bottom: 5px !important;" @click="data.toggleDetails">{{ data.detailsShowing ? 'Скрыть' : 'Показать' }} подробности</b-button>
                </template>
                <template #row-details="data">
                    <b-row>
                        <b-col>
                            <text-row v-if="data.item.status.id === 'rejected'" v-model="data.item.proposal_details.decline_status_name" label="Причина отклонения" compact />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-button variant="custom-outline-secondary" class="fs-14 my-1" size="sm" :href="rnpCheckLink + data.item.supplier.inn" target="_blank">Проверить в РНП</b-button>
                            <download-organization-analytics-report-button class="ml-2" :organization="data.item.supplier" />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <text-row-raw label="Контакты" compact>
                                <ul class="fs-14 grey-color my-auto col-form-label">
                                    <li>ФИО:&nbsp;<b v-text="data.item.proposal_details.contact_name"></b></li>
                                    <li>
                                        Телефон:&nbsp;<a class="atmo-button-link" :href="`tel:+${data.item.proposal_details.contacts.phone}`">{{ $parsePhone(data.item.proposal_details.contacts.phone) }}</a>
                                    </li>
                                    <li>Email:&nbsp;<a class="atmo-button-link" :href="`mailto:${data.item.proposal_details.contacts.email}`" v-text="data.item.proposal_details.contacts.email"></a></li>
                                </ul>
                            </text-row-raw>
                            <text-row-single-document compact label="Карточка предприятия" v-model="data.item.proposal_details.organization_card" />
                            <text-row-raw label="Требования к участникам закупки" compact>
                                <ul class="fs-14 grey-color my-auto col-form-label">
                                    <li v-if="data.item.proposal_details.supplier_requirements_confirmed.supplier_reqs_a">
                                        <i class="atmo-icon-checkbox"></i>&nbsp;Поставщик подтвердил, что соответствует требованиям п.1 ч.1 ст.31 Федерального закона «О контрактной системе в сфере закупок товаров, работ, услуг для обеспечения государственных и муниципальных нужд» от 05.04.2013 №44-ФЗ.
                                        <ul>
                                            <li>Документы, подтверждающие соответствие требованиям:</li>
                                            <li v-for="(document, index) in data.item.proposal_details.supplier_requirements_confirmed.supplier_required_documents" :key="index">
                                                <ul class="ml-3">
                                                    <li>
                                                        {{ `${index + 1}. ${document.name} - ` }}
                                                        <a class="fs-14 atmo-button-link" :href="$getAttachmentDownloadLink(document.storedDocument.id)" target="_blank">{{ document.storedDocument.name }}</a>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                    <li v-if="data.item.proposal_details.supplier_requirements_confirmed.supplier_reqs_b">
                                        <i class="atmo-icon-checkbox"></i>&nbsp;Поставщик подтвердил, что соответствует требованиям п.3-5, 7-11 ч.1 ст.31 Федерального закона «О контрактной системе в сфере закупок товаров, работ, услуг для обеспечения государственных и муниципальных нужд» от 05.04.2013 №44-ФЗ.
                                    </li>
                                    <li v-if="data.item.proposal_details.supplier_requirements_confirmed.supplier_reqs_c">
                                        <i class="atmo-icon-checkbox"></i>&nbsp;Поставщик подтвердил, что соответствует требованиям ч.1.1 ст.31 Федерального закона «О контрактной системе в сфере закупок товаров, работ, услуг для обеспечения государственных и муниципальных нужд» от 05.04.2013 №44-ФЗ.
                                    </li>
                                    <li v-if="data.item.proposal_details.supplier_requirements_confirmed.supplier_reqs_sonko"><i class="atmo-icon-checkbox"></i>&nbsp;Поставщик подтвердил, что является <abbr title="Социально ориентированная некоммерческая организация">СОНКО</abbr>.</li>
                                    <li v-if="!!data.item.proposal_details.supplier_requirements_confirmed.additional_requirements && data.item.proposal_details.supplier_requirements_confirmed.additional_requirements.length > 0">
                                        <i class="atmo-icon-checkbox"></i>&nbsp;Поставщик подтвердил соответствие дополнительным требованиям приложив следующие документы:
                                        <ul>
                                            <li v-for="(requirement, index) in data.item.proposal_details.supplier_requirements_confirmed.additional_requirements" :key="index">
                                                <ul class="ml-3">
                                                    <li>
                                                        {{ `${index + 1}. ${requirement.description} (Причина: ${requirement.reason}) - ` }}
                                                        <a class="fs-14 atmo-button-link" :href="$getAttachmentDownloadLink(requirement.attachment.id)" target="_blank">{{ requirement.attachment.name }}</a>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </text-row-raw>
                            <text-row-single-document v-if="data.item.proposal_details.commercial_proposal" compact label="Коммерческое предложение" v-model="data.item.proposal_details.commercial_proposal" />
                            <text-row-documents compact label="Дополнительные документы" v-model="data.item.proposal_details.attachments" v-if="data.item.proposal_details.attachments.length > 0" />
                            <text-row-proposal-specification-table v-model="data.item.proposal_details.deliverables" :without-vat="data.item.proposal_details.without_vat">
                                <template #header>
                                    <span class="fs-14 fw-600" style="padding-left: 5px;">Спецификация:</span>
                                </template>
                            </text-row-proposal-specification-table>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col class="d-flex">
                            <b-button :disabled="data.item.status.id !== 'actual' || data.item.id === selectedProposalId" class="fs-14 my-1" size="sm" variant="custom-green" @click="selectWinner(data.item.id)">{{ data.item.id === selectedProposalId ? 'Выбран' : 'Выбрать' }} победителем</b-button>
                            <fl44-unknown-volume-reject-proposal-button class="ml-2" :purchase="purchase" :proposal="data.item" @refresh="refresh(data.item.id)" />
                        </b-col>
                    </b-row>
                </template>
                <template #empty>
                    <div class="w-100 text-center">Нет поданных предложений</div>
                </template>
            </b-table>
            <b-row>
                <b-col class="d-flex">
                    <b-button :disabled="$v.$invalid" class="fs-14 my-1" size="sm" variant="custom-green" @click="isModalVisible = true">Сформировать итоговый протокол</b-button>
                    <span v-if="$v.$invalid" class="text-danger pl-3 pt-0 fs-14 w-50 my-auto">Необходимо выбрать победителя или отклонить все предложения</span>
                </b-col>
            </b-row>
            <show-sign-modal v-if="signData.signature" :signature="signData.signature" :org-is-customer="signData.isCustomer" :show="showSignModal" @close="showSignModal = false" />
        </div>
        <b-modal ref="cancel-modal" v-model="isModalVisible" body-class="pt-0" header-class="pb-1" centered hide-header-close no-close-on-backdrop no-close-on-esc title="Уважаемый пользователь!">
            <p class="text-danger fw-600 fs-16 my-1">Обращаем Ваше внимание, что данное действие необратимо.</p>
            <p class="text-danger fw-600 fs-16 my-1">Нажимая кнопку "Сформировать итоговый протокол" Вы подтверждаете{{ hasNoProposals ? ', что данная закупка не состоялась' : ((hasRejectedAllProposals ? ' отклонение всех предложений' : ' выбор поставщика') + ' для данной закупки') }}.</p>
            <div slot="modal-footer" class="w-100 text-right">
                <b-overlay :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                    <b-button class="text-uppercase mr-2" variant="custom-outline-secondary" @click="isModalVisible = false">Отменить</b-button>
                </b-overlay>
                <b-overlay :show="isDataSending" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                    <b-button class="text-uppercase" variant="custom-green" @click="storeProtocol">Сформировать итоговый протокол</b-button>
                </b-overlay>
            </div>
        </b-modal>
    </div>
</template>

<script>

import { encodeBase64, saveAs } from '@progress/kendo-file-saver';
import DownloadOrganizationAnalyticsReportButton from '@/components/products/fl223/partials/download-organization-analytics-report-button.vue';
import TextRowProposalSpecificationTable from '@/components/proposals/partials/text-row-proposal-specification-table.vue';
import Fl44UnknownVolumeRejectProposalButton from '@/components/products/fl44-unknown-volume/partials/fl44-unknown-volume-reject-proposal-button.vue';
import { requiredIf } from 'vuelidate/lib/validators';

export default {
    name: 'fl44-unknown-volume-summarizing-block',
    components: { Fl44UnknownVolumeRejectProposalButton, TextRowProposalSpecificationTable, DownloadOrganizationAnalyticsReportButton },
    props: {
        purchase: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            loading: true,
            showSignModal: false,
            signData: {
                signature: null,
                isCustomer: false
            },
            proposals: [],
            selectedProposalId: null,
            fields: [
                { key: 'index', label: '№', tdClass: 'align-text-top text-nowrap py-1', thClass: 'py-1', thStyle: { width: '1px' } },
                { key: 'supplier', label: 'Поставщик', tdClass: 'align-text-top py-1', thClass: 'py-1', thStyle: { width: '490px' } },
                { key: 'status', label: 'Статус', tdClass: 'align-text-top py-1', thClass: 'text-nowrap py-1' },
                { key: 'created_at', label: 'Дата и время подачи', tdClass: 'align-text-top py-1 text-nowrap', thClass: 'py-1', thStyle: { width: '155px' } },
                { key: 'rating', label: 'Рейтинг ИС', tdClass: 'align-text-top text-center py-1 text-nowrap', thClass: 'text-center py-1', thStyle: { width: '100px' } },
                { key: 'actions', label: '', tdClass: 'align-text-top py-1 text-nowrap text-right', thClass: 'py-1 text-nowrap text-right', thStyle: { width: '1px' } }
            ],
            isModalVisible: false,
            isDataSending: false,
        };
    },
    async mounted() {
        await this.fillData();
    },
    methods: {
        async fillData() {
            this.proposals = await this.$api.personal.fl44UnknownVolume.getProposals(this.purchase.id);
            this.loading = false;
        },
        async refresh(proposalId) {
            if (this.selectedProposalId === proposalId) {
                this.selectedProposalId = null;
            }
            await this.fillData();
        },
        selectWinner(proposalId) {
            this.selectedProposalId = proposalId;
        },
        showSign(isCustomer, signature) {
            this.signData.isCustomer = isCustomer;
            this.signData.signature = signature;
            this.showSignModal = true;
        },
        downloadXml(signature, isCustomer, fileName) {
            const downloadData = 'data:text/plain;base64,' + encodeBase64(signature);
            const downloadFileName = (isCustomer ? 'customer_' : 'supplier_') + fileName + '.xml';
            saveAs(downloadData, downloadFileName);
        },
        async storeProtocol() {
            this.isDataSending = true;
            if (await this.$api.personal.fl44UnknownVolume.storeProtocol(this.purchase.id, { selected_proposal_id: this.selectedProposalId })) {
                this.isModalVisible = false;
                this.$emit('refresh', this.selectedProposalId ? (this.$route.hash !== '#atmo-contract' ? '#atmo-contract' : null) : (!this.$route.hash ? null : ''));
            }
            this.isDataSending = false;
        }
    },
    validations() {
        return {
            selectedProposalId: {
                required: requiredIf(() => this.proposals.filter((item) => item.status.id === 'actual').length > 0),
            },
        }
    },
    computed: {
        rnpCheckLink() {
            return 'https://zakupki.gov.ru/epz/dishonestsupplier/search/results.html?searchString=';
        },
        hasNoProposals() {
            return this.proposals.length === 0;
        },
        hasRejectedAllProposals() {
            return this.proposals.length > 0 && this.proposals.length === this.proposals.filter((item) => item.status.id === 'rejected').length;
        }
    }
};
</script>
